import { useMemo } from "react";
import { useDispatch } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { getAllTenantFieldOptions } from "../../../../actions/Tenants/actions";
import useConfig from "../../../../actions/Tenants/config/configHook";
import { usePagination } from "../../../../hooks";
import { callAvosApi } from "../../../../utils/useAvosApiHook";
import { ImportExportModal } from "../../../Helper/ImportExportModal";
import { useImportExport } from "../../../Helper/ImportExportModal/useImportExport";
import { PageBase, PageBaseTableColumns } from "../PageBase";
import { definedGrowerFormCreate } from "./defineGrowerFormCreate";
import { Grower } from "./types";
import { validateImportItems } from "../utils";

const endpoint = "/grower/";

const GrowerPage = () => {
    const dispatch = useDispatch();
    const config = useConfig();
    const queryClient = useQueryClient();

    const { offset, limit } = usePagination();

    const importFields = [
        { label: "name", description: "Grower name", required: true },
        {
            label: "country",
            description: <span>Only countries configured in <Link to={`/tenants/${config?.tenant_id}/form-field-options/country`}>country form options</Link> are valid.</span>,
            required: true,
        },
        {
            label: "fruit_types",
            description: <span>Only fruit types configured in <Link to={`/tenants/${config?.tenant_id}/form-field-options/fruit_type`}>fruit type form options</Link> are valid. Separate multiple values with comma.</span>,
            required: true,
        },
        { label: "ggn", description: "GGN", required: true },
    ];

    const getGrowers = async (offset, limit) => {
        const res = await callAvosApi(`${endpoint}?limit=${limit}&offset=${offset}`);
        return res.data;
    };

    const { data: growers, isLoading } = useQuery({
        queryKey: ["growers", offset, limit],
        queryFn: () => getGrowers(offset, limit),
    });

    const invalidateQueries = () => {
        queryClient.invalidateQueries({ queryKey: ["growers"], exact: false });
        queryClient.invalidateQueries({ queryKey: ["all-growers"], exact: false });
        queryClient.invalidateQueries({ queryKey: ["suppliers"], exact: false });
        queryClient.invalidateQueries({ queryKey: ["all-suppliers"], exact: false });
    };

    const { mutate: saveGrower } = useMutation({
        mutationFn: async (grower: Grower) => {
            return callAvosApi(`${endpoint}${grower.id ?? ""}`, {
                method: grower.id ? "PUT" : "POST",
                data: grower,
            });
        },
        onSuccess: () => {
            invalidateQueries();
        },
    });

    const { mutate: deleteGrower } = useMutation({
        mutationFn: async (id: string) => {
            return callAvosApi(`${endpoint}${id}`, {
                method: "DELETE",
            });
        },
        onSuccess: () => {
            invalidateQueries();
        },
    });

    const onSaveGrower = async (grower: Grower) => {
        await saveGrower(grower);
        dispatch(getAllTenantFieldOptions(config.tenant_id) as any);
    };

    const onDeleteGrower = async (id: string) => {
        await deleteGrower(id);
        dispatch(getAllTenantFieldOptions(config.tenant_id) as any);
    };

    const growerState = useMemo(() => {
        return {
            items: growers || { results: [], count: 0 },
            isLoading,
            filter: { offset, limit },
            localFiltering: false,
            reload: false
        };
    }, [growers, isLoading, offset, limit]);

    const tableRow: PageBaseTableColumns<Grower>[] = [
        {
            label: "Grower Name",
            name: "label",
            lg: 3,
        },
        {
            label: "Country",
            name: "country",
            display_component(entity:Grower) {
                return config.get_form_option_label("country", entity.country);
            },
            lg: 2,
        },
        {
            label: "Fruit(s)",
            name: "fruit_types",
            display_component(entity:Grower) {
                return entity.fruit_types.map((fruit_type) => (config.get_form_option_label("fruit_type", fruit_type)) || []).join(", ");
            },
            lg: 2,
        },
        {
            label: "GGN",
            name: "ggn",
            lg: 2,
        }
    ];

    const createGrowerForm = definedGrowerFormCreate(onSaveGrower, onDeleteGrower);
    const createForms = [createGrowerForm];

    const entityName = "grower";
    const modalProps = useImportExport({
        entityName,
        importFields,
        affectedQueries: ["suppliers", "all-suppliers"],
        uniqueIdentifierKey: "ggn",
        validateImportItems: ({ importData, existingItems }) => validateImportItems(entityName, { importData, existingItems, config }),

    });

    return <PageBase<Grower>
        title="Grower"
        tableColumns={tableRow}
        actionsColumnWidth={3}
        createForms={createForms as any}
        state={growerState}
        actions={<ImportExportModal entityName={entityName} importFields={importFields} {...modalProps} />}
    />;
};

export default GrowerPage;
